import React, { createContext, useContext, useState, useCallback } from 'react';

const MomentUploadContext = createContext(null);

export function MomentUploadProvider({ children }) {
  const [uploadState, setUploadState] = useState(null);

  const startUpload = useCallback((uploadInfo) => {
    setUploadState({
      id: Date.now().toString(),
      status: 'uploading',
      progress: 0,
      restaurantName: uploadInfo.restaurantName,
      retryCallback: uploadInfo.retryCallback, // Store retry callback
      ...uploadInfo,
    });
  }, []);

  const updateProgress = useCallback((progress) => {
    setUploadState((prev) => {
      if (!prev) return null;
      return { ...prev, progress };
    });
  }, []);

  const completeUpload = useCallback(() => {
    setUploadState((prev) => {
      if (!prev) return null;
      return { ...prev, status: 'completed', progress: 100 };
    });
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setUploadState(null);
    }, 3000);
  }, []);

  const failUpload = useCallback((error) => {
    setUploadState((prev) => {
      if (!prev) return null;
      return { ...prev, status: 'failed', error: error?.message || 'Upload failed' };
    });
    // Don't auto-hide on error - let user retry
  }, []);

  const clearUpload = useCallback(() => {
    setUploadState(null);
  }, []);

  const retryUpload = useCallback(() => {
    setUploadState((prev) => {
      if (!prev || !prev.retryCallback) return prev;
      // Call the retry callback
      prev.retryCallback();
      // Reset to uploading state
      return {
        ...prev,
        status: 'uploading',
        progress: 0,
        error: null,
      };
    });
  }, []);

  return (
    <MomentUploadContext.Provider
      value={{
        uploadState,
        startUpload,
        updateProgress,
        completeUpload,
        failUpload,
        clearUpload,
        retryUpload,
      }}
    >
      {children}
    </MomentUploadContext.Provider>
  );
}

export function useMomentUpload() {
  const context = useContext(MomentUploadContext);
  if (!context) {
    throw new Error('useMomentUpload must be used within MomentUploadProvider');
  }
  return context;
}

